import {
  BodyText,
  Button,
  Icon,
  IconSet,
  Layout,
  LinkText,
  TabPanel,
} from '@postscript/components';
import DisplayCard from '../../Layout/DisplayCard';
import {
  StyledTabPanelIntro,
  TwoColumnLayout,
} from '../../Layout/SimpleStyledComponents';
import TokenBlock from '../../Layout/TokenBlock';
import { FoundationsProps } from '../Foundations';

const FoundationsLayout = ({ selected }: FoundationsProps): JSX.Element => {
  return (
    <TabPanel id="foundations-layout" selected={selected}>
      <StyledTabPanelIntro>
        <BodyText size="medium">
          Our spacing system creates a consistent baseline unit for our brand:
          6px. Adjust it by variable ratios to control rhythm, density, and the
          grid system.
        </BodyText>
      </StyledTabPanelIntro>

      <TwoColumnLayout style={{ marginBottom: 'var(--spacing-4)' }}>
        <DisplayCard
          heading="Alignment and spacing"
          advancedDescription={
            <>
              <BodyText size="small">
                Margins and paddings in and around elements should be in
                increments of our 6px baseline—including the horizontal padding
                inside elements. This maintains harmony between vertical and
                horizontal spacing. Components should control their own internal
                spacing, whereas the space between components should be
                determined by the template of the page.
              </BodyText>
              <BodyText size="small">
                When laying out elements on a page, watch the edges and ensure
                proper alignment. Don&apos;t allow elements to weave unnaturally
                around a page.
              </BodyText>
            </>
          }
          emoji="triangular-ruler"
        />
        <DisplayCard
          heading="CSS Variables"
          advancedDescription={
            <>
              <BodyText size="small">
                Spacing variables are available in increments of 6px, used
                primarily for quickness and consistency in margins and padding.
              </BodyText>
              <Layout gap="0">
                <TokenBlock name="6px" tight variable="spacing-1" />
                <TokenBlock name="12px" tight variable="spacing-2" />
                <TokenBlock name="18px" tight variable="spacing-3" />
                <TokenBlock name="24px" tight variable="spacing-4" />
              </Layout>
              <Layout gap="0">
                <TokenBlock name="30px" tight variable="spacing-5" />
                <TokenBlock name="36px" tight variable="spacing-6" />
                <TokenBlock name="42px" tight variable="spacing-7" />
                <TokenBlock name="48px" tight variable="spacing-8" />
              </Layout>
              <Layout gap="0">
                <TokenBlock name="54px" tight variable="spacing-9" />
                <TokenBlock name="60px" tight variable="spacing-10" />
              </Layout>
            </>
          }
          emoji="alien-monster"
        />
      </TwoColumnLayout>
      <TwoColumnLayout style={{ marginBottom: 'var(--spacing-4)' }}>
        <DisplayCard
          heading="Layout grids"
          advancedDescription={
            <>
              <BodyText size="small">
                Layouts in the Postscript ecosystem are generally based on
                12-column grids with a 6px baseline. Each department of
                Postscript will have custom layouts to achieve their designs,
                based off these basic rules. Be sure to check the{' '}
                <LinkText
                  href="https://components.postscript.io/?path=/story/%E2%9A%A1%EF%B8%8F-product-page-layout--breakpoints-and-media-queries"
                  target="_blank"
                  rel="noreferrer"
                >
                  Product layout documentation
                </LinkText>{' '}
                to understand their individual needs and use cases.
              </BodyText>
            </>
          }
          emoji="brick"
        />
        <DisplayCard
          heading="Responsiveness"
          advancedDescription={
            <>
              <BodyText size="small">
                Our users work primarily on desktops (most often at 1440 x 900),
                and many of our users access the application via their phones.
                In the product, strive to make non-complex flows fully
                responsive, designing for the{' '}
                <LinkText
                  href="https://components.postscript.io/?path=/story/%E2%9A%A1%EF%B8%8F-product-page-layout--breakpoints-and-media-queries"
                  target="_blank"
                  rel="noreferrer"
                >
                  documented breakpoints
                </LinkText>
                . More complex user interaction where full optimization is
                impractical—such as Flows or Popup design—should be at minimum
                accessible and navigable.
              </BodyText>
            </>
          }
          emoji="mobile-phone-with-arrow"
        />
      </TwoColumnLayout>

      <DisplayCard
        heading="Content width"
        advancedDescription={
          <>
            <div style={{ maxWidth: '612px' }}>
              <BodyText size="small">
                Layout modules can be as wide or narrow as you need them to be.
                However, avoid allowing text and layouts to simply expand
                full-width in a very wide container. This reduces readability,
                looks unintentional, and can reduce usability by adding
                unnecessarily large distances between related elements
                (violating{' '}
                <LinkText
                  href="https://lawsofux.com/en/fittss-law"
                  target="_blank"
                  rel="noreferrer"
                >
                  Fitt&apos;s Law
                </LinkText>
                ). In general, aim for a base width of{' '}
                <LinkText
                  href="https://www.w3.org/TR/UNDERSTANDING-WCAG20/visual-audio-contrast-visual-presentation.html"
                  target="_blank"
                  rel="noreferrer"
                >
                  80 characters
                </LinkText>{' '}
                for your text, or at least strive to preserve the
                customers&apos; ability to reduce the screen size to achieve 80
                characters.
              </BodyText>
            </div>

            <div
              style={{
                padding: 'var(--spacing-2)',
                marginTop: 'var(--spacing-4)',
                border: '1px solid var(--border-color-dim)',
                borderRadius: 'var(--border-radius-small)',
              }}
            >
              <Layout
                gap="var(--spacing-1)"
                valign="top"
                style={{
                  marginBottom: 'var(--spacing-4)',
                }}
              >
                <Icon
                  color="var(--error-color)"
                  component={IconSet.Close}
                  size="large"
                />
                <BodyText
                  size="x-small"
                  style={{ fontSize: '10px', marginTop: '3px' }}
                >
                  This paragraph is very long, stretching the full width of its
                  container. At large sizes, this text will grow too long to
                  read comfortably. The CTA is also placed very far to the
                  right, decreasing its visibility and relation to the material.
                </BodyText>
                <Button size="small" style={{ transform: 'scale(.7)' }}>
                  Click
                </Button>
              </Layout>
              <Layout
                gap="var(--spacing-1)"
                valign="top"
                style={{
                  maxWidth: '462px',
                }}
              >
                <Icon
                  color="var(--mint-core)"
                  component={IconSet.Checkmark}
                  size="large"
                />
                <BodyText
                  size="x-small"
                  style={{ fontSize: '10px', marginTop: '3px' }}
                >
                  This paragraph is also long, but has a max-width to help limit
                  the character count of each line. This makes it easier to
                  read, and helps keep the CTA present and noticeable.
                </BodyText>
                <Button size="small" style={{ transform: 'scale(.7)' }}>
                  Click
                </Button>
              </Layout>
            </div>
          </>
        }
        emoji="open-hands"
      />
    </TabPanel>
  );
};

export default FoundationsLayout;
